import { useMemo } from 'react';
import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { projectTemplateApi } from '@src/apis/admin';
import type {
  ProjectTemplateInfoResponseModel,
  ProjectTemplateInfoResponseModelArrayApiSuccessResponseModel,
} from '@uniquegood/realworld-admin-interface';

type TemplateListQueryKey = readonly ['templates'];

export interface UseTemplateListOptions {
  /**
   * React Query 옵션을 덮어씁니다.
   * `queryKey`와 `queryFn`은 내부에서 관리하므로 제외했습니다.
   */
  queryOptions?: Omit<
    UseQueryOptions<
      ProjectTemplateInfoResponseModel[],
      Error,
      ProjectTemplateInfoResponseModel[],
      TemplateListQueryKey
    >,
    'queryKey' | 'queryFn'
  >;
}

/**
 * 템플릿 목록 조회 훅
 *
 * @description
 * - 프로젝트 템플릿 목록을 조회합니다.
 * - React Query 옵션은 `queryOptions`를 통해 세밀하게 조정할 수 있습니다.
 */
export const useTemplateList = (
  options: UseTemplateListOptions = {}
): UseQueryResult<ProjectTemplateInfoResponseModel[]> => {
  const { queryOptions } = options;
  const { staleTime, ...restQueryOptions } = queryOptions ?? {};

  const queryKey = useMemo<TemplateListQueryKey>(() => ['templates'] as const, []);

  return useQuery<
    ProjectTemplateInfoResponseModel[],
    Error,
    ProjectTemplateInfoResponseModel[],
    TemplateListQueryKey
  >({
    queryKey,
    queryFn: async () => {
      const { data } = await projectTemplateApi.getTemplates();
      return data.data ?? [];
    },
    staleTime: staleTime ?? 30 * 1000,
    ...restQueryOptions,
  });
};

